import { useGameDriver } from "@/hooks/useGameDriver";
import { useGameStore } from "@/store/gameStore";
import { useUiStore } from "@/store/uiStore";
import SeatPanel from "./SeatPanel";
import PlayerPanel from "./PlayerPanel";
import CenterStage from "./CenterStage";
import ActionBar from "./ActionBar";
import LogPanel from "./LogPanel";
import Hand from "./Hand";
import TargetPicker, { useTargetPicker } from "./TargetPicker";
import DiscardPicker from "./DiscardPicker";
import ResponsePrompt, { ResponseWaitingBanner } from "./ResponsePrompt";
import PickupPrompt, { PickupWaitingBanner } from "./PickupPrompt";
import RescuePrompt from "./RescuePrompt";
import GameOverScreen from "./GameOverScreen";
import DrawPile from "./DrawPile";
import JudgmentOverlay from "./JudgmentOverlay";
import TooltipProvider from "./TooltipProvider";
import "@/styles/board.css";
import "@/styles/seat.css";
import "@/styles/card.css";
import "@/styles/prompt.css";

export default function Board() {
  useGameDriver();
  const state = useGameStore((s) => s.state);
  const view = useUiStore((s) => s.view);
  const { isActive: targetActive, targetIds, pickTarget } = useTargetPicker();

  if (!state) return null;

  if (state.phase === "game-over") {
    return <GameOverScreen />;
  }

  const players = state.players;
  const p0 = players[0];
  const p1 = players[1];
  const p2 = players[2];
  const p3 = players[3];
  const currentId = players[state.currentPlayerIdx].id;

  // TargetPicker 注入：每个座席/玩家面板的目标性
  const isTarget = (id: string) => targetActive && targetIds.includes(id);
  const onTarget = (id: string) => () => pickTarget(id);

  if (view === "mobile") {
    return (
      <TooltipProvider>
        <div className="board board--mobile">
        <div className="board__row board__row--top">
          <SeatPanel
            player={p2}
            isCurrent={p2.id === currentId}
            isTargetable={isTarget(p2.id)}
            onTarget={onTarget(p2.id)}
          />
          <SeatPanel
            player={p1}
            isCurrent={p1.id === currentId}
            isTargetable={isTarget(p1.id)}
            onTarget={onTarget(p1.id)}
          />
        </div>
        <CenterStage />
        <TargetPicker />
        <div className="draw-pile-wrap">
          <DrawPile />
        </div>
        <div className="board__row board__row--mid">
          <SeatPanel
            player={p3}
            isCurrent={p3.id === currentId}
            isTargetable={isTarget(p3.id)}
            onTarget={onTarget(p3.id)}
          />
          <PlayerPanel
            player={p0}
            isCurrent={p0.id === currentId}
            isTargetable={isTarget(p0.id)}
            onTarget={onTarget(p0.id)}
          />
        </div>
        <Hand />
        <ActionBar />
        <DiscardPicker />
        <ResponsePrompt />
        <ResponseWaitingBanner />
        <PickupPrompt />
        <PickupWaitingBanner />
        <RescuePrompt />
        <JudgmentOverlay />
        <LogPanel />
      </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div className="board board--desktop">
      <div className="board__cell--top">
        <SeatPanel
          player={p2}
          isCurrent={p2.id === currentId}
          isTargetable={isTarget(p2.id)}
          onTarget={onTarget(p2.id)}
        />
      </div>
      <div className="board__cell--left">
        <SeatPanel
          player={p1}
          isCurrent={p1.id === currentId}
          isTargetable={isTarget(p1.id)}
          onTarget={onTarget(p1.id)}
        />
      </div>
      <div className="board__cell--center">
        <CenterStage />
        <TargetPicker />
        <DiscardPicker />
        <DrawPile />
        <ResponsePrompt />
        <ResponseWaitingBanner />
        <PickupPrompt />
        <PickupWaitingBanner />
        <RescuePrompt />
        <JudgmentOverlay />
      </div>
      <div className="board__cell--right">
        <SeatPanel
          player={p3}
          isCurrent={p3.id === currentId}
          isTargetable={isTarget(p3.id)}
          onTarget={onTarget(p3.id)}
        />
      </div>
      <div className="board__cell--bottom">
        <PlayerPanel
          player={p0}
          isCurrent={p0.id === currentId}
          isTargetable={isTarget(p0.id)}
          onTarget={onTarget(p0.id)}
        />
        <Hand />
        <ActionBar />
        <LogPanel />
      </div>
    </div>
    </TooltipProvider>
  );
}
